import React, { useEffect, useMemo, useState } from "react";
import { useZipCodeValue, useRsnFeeValue, useApiPaths, useZipCode, useRsnFee, useCountyOptions } from "@directv-digital/dds.server.application-context";
import DtvTheme from "@directv-digital/dds.ui.dtv-theme";

const bps = DtvTheme.breakpoints;
import { ZipcodeModuleProps, defaultCiConfig } from "./zipcode-module.props";
import { useLocationHref } from "src/hooks/useLocationHref";
import { modalLinkPosition } from "src/utils/link-click";

// APIs
import LocalRsnChnlService, { LocalRsnApiResponse, LocalRsnChannel, ProductType } from "@directv-digital/dds.apis.local-rsn-chnls";
import {
  buildLocalChannelObject,
  fetchChannelsServiceLocals,
  type LocalsPlatform
} from "src/apis/channels-service-locals";
import {
  getZipcodeWithSignature,
  ZipcodeValidator,
  getDmaCookies,
  setDmaCookies,
  getCookie,
  isValidRsnFee
} from "@directv-digital/dds.services.cookie-service";
import {
  getCountiesByZipcode,
  County
} from "@directv-digital/dds.apis.county-lookup";

import DtvText from "@directv-digital/dds.ui.dtv-text";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import Button from "@directv-digital/fusion.atoms.button";
import TextField from "src/components/video-lab/text-field";
import TextFieldDropdown from "src/components/video-lab/text-field-dropdown";
import DtvAlert from "@directv-digital/fusion.molecules.alert";
import type { Breakpoint } from "@mui/material/styles";

const COUNTY_API_DELAYED_MS = 5000; // 5 seconds
const COUNTY_API_TIMEOUT_MS = 30000; // 30 seconds
const SUCCESS_MSG_TIMEOUT_MS = 5000; // 5 seconds

/**
 * [WAT-12043 / YFH-AMP121] Success-message text used by the visible
 * DtvAlert (the DOM wrapper's `aria-label`, the alert's own `ariaLabel`
 * and `title`). Kept as a single constant so a future AMP121 follow-up
 * that re-introduces a screen-reader-specific announcer can source the
 * same string. Not CMS-driven today; if that changes the constant
 * becomes a prop.
 */
const UPDATE_SUCCESS_MSG = "Update Successful";

/** A county's TV market (dtv + Nielsen DMA); "" for a county with no dtvDMA. */
const marketKey = (county: County | null | undefined) => (
  county?.dtvDMA ? `${county?.dtvDMA}|${county?.nielsenDMA}` : ""
);

/** Cache key for one ZIP's lookup in one market (RSNs depend on the ZIP). A
 *  county with no market has no key: nothing is looked up or cached for it,
 *  and Update settles it at once with empty lists. */
const cacheKey = (zip: string, county: County | null | undefined) => (
  marketKey(county) ? `${zip}|${marketKey(county)}` : ""
);

/** What a failed or keyless lookup delivers: pages destructure it, so never null. */
const noLocalRsn = () => ({
  localChannels: [],
  rsnChannels: []
}) as LocalRsnApiResponse;

/** 4th argument on a failure delivery, so a page keeps its error state. */
const FAILED_DELIVERY = { failed: true };

/** Location pin icon — Figma node 13987:15711 "pinpoint" (Agent Comparison Tool
 *  design, node 13987:15991 "ZIP Code locator"). Not in @directv-digital/fusion.atoms.icons,
 *  so it's inlined the same way the toggle chevron below is. Color follows the
 *  existing mode-based `changeTextColor` switch, not hardcoded to the light-mode
 *  Figma hex, so dark-mode consumers keep working. */
const LocationPinIcon = ({ color }: { color: string }) => (
  <svg fill="none" height="14" viewBox="0 0 9.77778 13.3333" width="10" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4.89031 0.000159775C3.89934 -0.00795518 2.92965 0.293225 2.11178 0.863168C1.2939 1.43311 0.667089 2.24444 0.315537 3.1881C-0.0360158 4.13176 -0.0954122 5.16244 0.145397 6.14149C0.386205 7.12054 0.915592 8.001 1.66243 8.66442C2.08062 9.06269 2.45244 9.50868 2.77046 9.99357H2.77311L4.62012 13.1587C4.64411 13.2111 4.68228 13.2553 4.73015 13.2862C4.77802 13.3172 4.83362 13.3335 4.89031 13.3333C4.94722 13.3328 5.0028 13.3159 5.05068 13.2845C5.09855 13.2531 5.1367 13.2086 5.16081 13.1561L7.00622 9.99006C7.32567 9.50671 7.69827 9.06212 8.11681 8.66491C8.86307 8.00134 9.392 7.12101 9.63256 6.14224C9.87312 5.16348 9.81373 4.13317 9.46242 3.18978C9.11111 2.24639 8.48469 1.43513 7.66735 0.865007C6.85 0.294887 5.88094 -0.00719044 4.89031 0.000159775ZM7.83016 6.06994C7.80797 6.12753 7.78361 6.18426 7.75794 6.24008C7.50426 6.79691 7.09949 7.2682 6.59139 7.59838C6.08328 7.92857 5.49303 8.10383 4.89031 8.10352C4.4746 8.10851 4.06197 8.02943 3.67649 7.87086C3.29101 7.71228 2.9403 7.47736 2.64461 7.17973C2.34891 6.88209 2.11418 6.52765 1.95396 6.13694C1.79373 5.74623 1.71117 5.32705 1.71117 4.90363C1.71117 4.48021 1.79373 4.06097 1.95396 3.67026C2.11418 3.27955 2.34891 2.92511 2.64461 2.62748C2.9403 2.32984 3.29101 2.09492 3.67649 1.93634C4.06197 1.77777 4.4746 1.6987 4.89031 1.70368C5.20855 1.70346 5.52499 1.75197 5.82905 1.84765C5.92823 1.87866 6.0248 1.91501 6.1192 1.95533C6.87556 2.27915 7.47873 2.88968 7.80138 3.65798C8.12288 4.42665 8.13327 5.29355 7.83016 6.06994Z"
      fill={color}
    />
  </svg>
);

export const ZipcodeModule = React.memo((
  props: ZipcodeModuleProps
) => {
  const {
    id = "Zipcode-Module",
    mode = "dark",
    size = "medium",
    position = "center",
    onSearchButtonClick = () => ({}), // empty function if not provided
    onRsnFeeConfirmed = () => ({}),
    onRsnFeeApiError: _onRsnFeeApiError = () => ({}),
    onLocalRsnChnlsConfirmed = null,
    onLocalRsnChnlsApiError = () => ({}),
    onLocalRsnChnlApiLoading = () => ({}),
    onCacheConfirmed = () => ({}),
    onCountyApiError = () => ({}),
    localRsnApiOptions = {
      satellite: false,
      stream: true
    },
    initData = null,
    isConditional,
    startEmpty = false,
    perZipRsnFee = false,
    resetSignal,
    fixedCountyWidth = false,
    ciConfig = defaultCiConfig,
    componentType: _componentType
  } = props;
  try {
    // Generated by Copilot- Start - 2026-04-01 20:31:38
    const appContextZip = useZipCodeValue() || "";
    const appContextRsnFee = useRsnFeeValue() || "";
    const apiPaths = useApiPaths();
    const { update: updateZipCode = () => undefined, value: zipCodeValue } = useZipCode() ?? {};
    const { update: updateRsnFeeCtx = () => undefined } = useRsnFee() ?? {};
    const { value: countyOptionsValue, update: updateCountyOptions = () => undefined } = useCountyOptions() ?? {};
    // Generated by Copilot- End - 2026-04-01 20:31:38
    /** Component */
    const inputRef = React.useRef() || {
      current: { focus: () => Promise.resolve() }
    };
    const [isVisible, setIsVisible] = useState<boolean>(startEmpty);
    const [showSuccessMsg, setShowSuccessMsg] = useState<boolean>(false);
    const successMsgTimeoutRef = React.useRef<ReturnType<
      typeof setTimeout
    > | null>(null);

    /** Text display */
    // Generated by Copilot- Start - 2026-04-15 14:21:57
    const [displayZip, setDisplayZip] = useState<string>(
      startEmpty ? "" : ((isConditional && initData?.zipcode) ? initData.zipcode : (appContextZip || ""))
    );
    // Generated by Copilot- End - 2026-04-15 14:21:57
    const [displayCounty, setDisplayCounty] = useState<County | null>(null);

    /** Zipcode/County Fields */
    const zipcodeInputRef = React.useRef<FusionTextFieldRef>(null);
    // Numbers each locals lookup. An answer that lands after a newer lookup
    // started never replaces the newer one's cache, and is dropped entirely once a
    // newer answer has reached the page; until then it still delivers, so typing a
    // new ZIP before the first answer arrives cannot leave the page with no lineup
    // at all.
    const localeRequestSeq = React.useRef(0);
    const localeDeliveredSeq = React.useRef(0);
    // The newest lookup owns the spinner: an older one settling (or hanging) can
    // neither hide nor hold a newer one's spinner.
    const latestLocalsCall = React.useRef(0);
    // Generated by Copilot- Start - 2026-04-15 14:21:57
    const [inputZip, setInputZip] = useState<string>(
      startEmpty ? "" : ((isConditional && initData?.zipcode) ? initData.zipcode : (appContextZip || ""))
    );
    // Generated by Copilot- End - 2026-04-15 14:21:57
    const [zipInputError, setZipInputError] = useState<boolean>(false);
    const [countyOptions, setCountyOptions] = useState<County[] | null>(null);
    const [lastCountyOptions, setLastCountyOptions] = useState<County[] | null>(
      null
    );
    const [countySelection, setCountySelection] = useState<County | null>(null);
    const [countySelectionError, setCountySelectionError] =
      useState<boolean>(false);
    const [_changeFocused, setChangeFocused] = useState<boolean>(false);
    // True when rendered inside an iframe (the Channel Lineup modal). Read after
    // mount, so SSR and the first client render agree.
    const [isInIframe, setIsInIframe] = useState<boolean>(false);

    /** API State */
    const [isCountyApiLoading, setIsCountyApiLoading] = useState<boolean>(false);
    const [countyNoResults, setCountyNoResults] = useState<boolean>(false);
    const [countyApiHanging, setCountyApiHanging] = useState<boolean>(false);
    const [countyApiError, setCountyApiError] = useState<boolean>(false);

    /** Prefetching Local/Rsn API **/
    /** when search button clicked and data is still being fetched, set flag
     *  true, once data comes, send callback turn back to false*/
    const [localRsnCallbackNeeded, setLocalRsnCallbackNeeded] =
      useState<boolean>(false);
    /** Save the latest value, to send when requested by HOC */
    const [cachedZipcode, setCachedZipcode] = useState<string>("");
    // DMAs of the current county selection, for the DMA cookies only. Channels
    // are matched to a market through cachedLocalRsn's keys, never through this.
    const [cachedDmas, setCachedDmas] = useState<{
      dma: string;
      nielsenDma: string;
    } | null>(null);
    // WAT-12673: lookups cached per ZIP + market (cacheKey), so channels only
    // ever go out for the lookup they came from. null means the lookup failed or
    // answered nothing; a missing key means it has not settled.
    const [cachedLocalRsn, setCachedLocalRsn] = useState<Record<string, LocalRsnApiResponse | null>>({});
    // The request in flight per cacheKey: one per key; a second lookUp for the
    // key reuses it.
    const inFlight = React.useRef<Record<string, Promise<LocalRsnApiResponse | null>>>({});
    // What the visitor last confirmed with Update. Only handleSearchButtonClick
    // writes it.
    const [confirmed, setConfirmed] = useState<{ key: string; county: County | null }>({
      key: "",
      county: null
    });
    const storeLineup = (key: string, data: LocalRsnApiResponse | null) => {
      if (key) {
        setCachedLocalRsn(prev => ({
          ...prev,
          [key]: data ?? null
        }));
      }
    };
    // Looks one ZIP + market up and caches the answer (null on failure). While a
    // request for the key is in flight, returns that request instead of a new one.
    const lookUp = (zip: string, county: County | null | undefined) => {
      const key = cacheKey(zip, county);
      const pending = inFlight.current?.[key];
      if (key && pending) {
        return pending;
      }
      const settle = (data: LocalRsnApiResponse | null) => {
        if (key && inFlight.current) {
          delete inFlight.current[key];
        }
        storeLineup(key, data);
      };
      const request = getLocalRsnChannels({
        zipcode: zip,
        dma: county?.dtvDMA,
        nielsenDma: county?.nielsenDMA
      }).then(
        data => {
          settle(data);
          return data;
        },
        error => {
          settle(null);
          throw error;
        }
      );
      if (key && inFlight.current) {
        inFlight.current[key] = request;
      }
      return request;
    };
    // A failure (null) goes out as empty lists flagged failed, never as null.
    const deliverLineup = (lineup: LocalRsnApiResponse | null, county: County | null | undefined) => (lineup ?
      onLocalRsnChnlsConfirmed?.(lineup, county?.dtvDMA, county?.nielsenDMA) :
      onLocalRsnChnlsConfirmed?.(noLocalRsn(), county?.dtvDMA, county?.nielsenDMA, FAILED_DELIVERY));

    /** Theming/Styling */
    const mainTextColor = mode === "dark" ? "#FFF" : "#102641";
    const changeTextColor = mode === "dark" ? "#1FBCF8" : "#2E70BD";
    const hAlignment =
      position === "center" ?
        "center" :
        position === "right" ?
          "flex-end" :
          "flex-start";
    const displayTextVariant =
      size === "small" ? "body2medium" : "subhead2medium";

    // Custom switch to use container queries for in-page module
    const up = React.useCallback(
      (key: number | Breakpoint) => (position === "left" ?
          `@container (min-width: ${
              typeof key === "number" ? key : bps[key]
            }px)` :
        bps.up(key)),
      [bps.up, position]
    );
    const down = React.useCallback(
      (key: number | Breakpoint) => (position === "left" ?
          `@container (max-width: ${
              typeof key === "number" ? key - 1 : bps[key] - 1
            }px)` :
        bps.down(key)),
      [bps.down, position]
    );
    const locationHrefurl = useLocationHref();
    // Replace missing values with defaults
    const {
      displayText,
      displayTooltip,
      locationPillPlaceholder,
      zipCodeInputErrorMsg,
      zipCodeLookupErrorMsg,
      countySelectionErrorMsg,
      countyApiHangingMsg,
      countyApiErrorMsg,
      drawerOpenText,
      drawerCloseText,
      zipCodeLabel,
      countyLabel,
      ctaLabel,
      addLocationCtaLabel,
      ctaLinkPosition,
      ctaLinkName
    } = useMemo(() => {
      // If ciConfig is null or undefined, return all defaults
      if (!ciConfig) {
        return defaultCiConfig;
      }

      return Object.keys(defaultCiConfig).reduce(
        (config, key) => ({
          ...config,
          [key]:
            ciConfig[key] === undefined || ciConfig[key] === null ?
              defaultCiConfig[key] :
              ciConfig[key]
        }),
        {} as ZipcodeModuleProps["ciConfig"]
      );
    }, [ciConfig]);

    // County has no "city" field, so the pill reads "County, State ZIP" (e.g.
    // "Fulton, GA 30324") instead of the Figma copy's literal "City, State ZIP".
    const locationPillLabel = useMemo(() => {
      if (displayZip && displayCounty) {
        return `${displayCounty.getName()}, ${displayCounty.state} ${displayZip}`;
      }
      return locationPillPlaceholder;
    }, [displayZip, displayCounty, locationPillPlaceholder]);

    /* --- Cache API Calls---------------------------------------------------------------------- */
    /**
     * WAT-11611 — dress a channels-service station in the shape beam's own
     * stations arrive in.
     *
     * This is NOT cosmetic. `LocalRsnChnlService.getChannels` does not hand back
     * the raw `LocalRsnChannel` its types advertise (`getChannels` is declared
     * `Promise<any>`): every station is first run through the service's
     * `buildChannelObject`, which adds `TYPE`, `categoryId`, `label`, `chnlNum`,
     * `id`, `callLetter`, `isStream`/`isSatellite` and `IsActive`. Consumers were
     * built against THAT shape and gate on it — `channel-pack-lineup.tsx:1427`
     * and `inpage-channel-pack-lineup.tsx:288` both derive `isLocal` from
     * `channel?.TYPE === "LOCAL"`, and the channel-listing row builder
     * (`ChnlListingPageV2/utils/utilityFunctions.ts:83-99`) reads `chnlNum`,
     * `categoryId` and `label`. Substituting bare stations made every one of
     * those read undefined, which emptied the per-genre local list and rebuilt
     * row ids as the literal string "0-undefined-".
     *
     * Calling the service's own builder — rather than restating its field list
     * here — is what keeps the two shapes in step if beam ever adds a field.
     *
     * The one field deliberately kept is the logo — ours is already resolved,
     * so the builder's icon-directory prefix would break it. That rule, and the
     * builder's unguarded `logo.startsWith`, live in `buildLocalChannelObject`,
     * which the CHLUP zipcode utils and channel-compare-tool-v3 call too.
     */
    // Which platforms' locals this module shows. Both flags set is beam's dual mode,
    // where beam merges stream and satellite into one list; the service keeps them
    // on separate envelopes, so ask for each and merge the same way.
    const serviceLocalPlatforms: [LocalsPlatform, ProductType][] = localRsnApiOptions?.stream && localRsnApiOptions?.satellite ?
      [["ott", ProductType.STREAM], ["satellite", ProductType.SATELLITE]] :
      [localRsnApiOptions?.satellite ? ["satellite", ProductType.SATELLITE] : ["ott", ProductType.STREAM]];

    // Resolves to beam-shaped stations, or null when the service had none on any
    // platform, which keeps beam's locals.
    const fetchServiceLocals = async (nielsenDma: string): Promise<any[] | null> => {
      // A caller that asked beam for no locals gets none from the service either.
      if (localRsnApiOptions?.local === false) {
        return null;
      }
      const perPlatform = await Promise.all(serviceLocalPlatforms.map(async ([platform, productType]) => (
        (await fetchChannelsServiceLocals(nielsenDma, platform))?.map?.(station => buildLocalChannelObject(station, productType)) ?? []
      )));
      const stations = perPlatform.flat();
      if (!stations.length) {
        return null;
      }
      return perPlatform.length > 1 ? LocalRsnChnlService?.mergeChannels?.(stations) ?? stations : stations;
    };

    const getLocalRsnChannels = async ({
      zipcode,
      dma,
      nielsenDma
    }): Promise<{ localChannels?: any[]; rsnChannels?: any[] }> => {
      const params = {
        context: { apiPaths },
        zipcode,
        dma,
        nielsenDma
      };
      onLocalRsnChnlApiLoading(true);
      latestLocalsCall.current += 1;
      const callId = latestLocalsCall.current;
      const clearLoading = () => {
        if (callId === latestLocalsCall.current) {
          onLocalRsnChnlApiLoading(false);
        }
      };

      // WAT-11611: ask channels-service for this market's local stations
      // ALONGSIDE the beam call, never instead of it. Beam still owns
      // `rsnChannels` — engineering cannot supply a DMA for RSN, so that half of
      // the response is untouched (AC3) — and only the `localChannels` half is
      // swapped: whenever the service returns stations they are shown and beam's
      // locals are dropped; beam's locals are used only if the service fails or
      // has none. Started here rather than awaited so the two requests overlap.
      //
      // The market key is `nielsenDma` because that is what the service keys its
      // locals map by (`LocalChannels.byDma`, Nielsen code e.g. "501"), not the
      // `dma` field beside it. Satellite and stream carry their locals on
      // separate platform envelopes, and `localRsnApiOptions` — already passed by
      // every call site to tell beam which tier to answer for — picks the same
      // tier(s) here.
      // Any failure here means "no service answer", so beam's locals always stand.
      const serviceLocals = fetchServiceLocals(nielsenDma).catch(error => {
        console.warn("[ZipcodeModule] channels-service locals failed, using beam", error?.message);
        return null;
      });

      // eslint-disable-next-line no-return-await
      return await LocalRsnChnlService.getChannels(params, localRsnApiOptions)
        .then(
          async (data: LocalRsnApiResponse) => {
            // Awaited before the spinner clears so a slower service response
            // cannot repaint the list after the tool has said it finished.
            const localsFromService = await serviceLocals;
            clearLoading();
            // No service stations for this market: beam's answer, untouched.
            if ((localsFromService?.length ?? 0) === 0) {
              return data;
            }
            return {
              ...data,
              localChannels: localsFromService
            };
          },
          async error => {
            // Beam failed, but the service is the source of truth for locals: its
            // stations still show (RSNs stay empty).
            const localsFromService = await serviceLocals;
            clearLoading();
            if ((localsFromService?.length ?? 0) > 0) {
              return {
                localChannels: localsFromService,
                rsnChannels: []
              };
            }
            throw new Error(error);
          }
        )
        ?.catch(err => {
          console.error("[ZipcodeModule] !! Local/Rsn API Error !!", err);
          onLocalRsnChnlsApiError(err);
          clearLoading();
          return Promise.reject(null);
        });
    };

    const getCounties = async (
      searchZipcode: string
    ): Promise<{ fetchedCounties?: County[] | null; fetchedZip?: string }> => {
      let delayTimeoutId: ReturnType<typeof setTimeout>;
      let failureTimeoutId: ReturnType<typeof setTimeout>;

      try {
        delayTimeoutId = setTimeout(() => {
          setCountyApiHanging(true);
        }, COUNTY_API_DELAYED_MS);

        const apiTimeout = new Promise<null>(() => {
          failureTimeoutId = setTimeout(() => Promise.reject("County API request timed out"), COUNTY_API_TIMEOUT_MS);
        });

        const fetchPromise = await getCountiesByZipcode(
          { apiPaths },
          searchZipcode
        ).then((resp: any) => {
          if (resp?.error || resp === undefined) {
            return Promise.reject("County data not found");
          }
          return Promise.resolve({
            fetchedCounties: resp,
            fetchedZip: searchZipcode
          });
        });

        return Promise.race([fetchPromise, apiTimeout]);
      } catch (err: any) {
        if ((err as Error)?.name === "AbortError") {
          console.error(
            "[ZipcodeModule] beamlocalchannellookupms -> getCountiesByZipcode timed out !! ",
            err
          );
        } else {
          console.error(
            "[ZipcodeModule] beamlocalchannellookupms -> getCountiesByZipcode API Error !! ",
            err
          );
        }
        onCountyApiError(err);
        return Promise.reject(err);
      } finally {
        clearTimeout(delayTimeoutId);
        clearTimeout(failureTimeoutId);
      }
    };
    /** ---------------------------------------------------------------------------------------- */
    /* --- Handle API Calls and data caching --- */
    const syncIncomingData = async ({
      contextZip,
      contextFee: _contextFee,
      dma,
      nielsenDma
    }: {
      contextZip: string;
      contextFee: string;
      dma: string;
      nielsenDma: string;
    }) => {
      const zipCode = getZipcodeWithSignature(contextZip);
      const isNewZip = displayZip !== zipCode.value;

      if (!isNewZip && displayCounty && isCountyApiLoading) {
        console.debug(
          "[ZipcodeModule] cancelled data sync - zip code and counties are equal to current.",
          id,
          displayZip,
          zipCode.value,
          contextZip
        );
        return;
      }

      // Always update display with the best found zip
      setDisplayZip(zipCode.value);

      console.debug(
        `[ZipcodeModule] syncing incoming data...\n`,
        `In Component id: ${id}\n`,
        `context zip: '${appContextZip}' currentZipcode: '${getCookie(
          "currentZipcode"
        )}'\n edgescape zip: '${
          decodeURIComponent(getCookie("x-dtv-edgescape") || "")
            ?.match(/\d{5}/)
            ?.join() || ""
        }' display zip: '${displayZip}'\n`,
        `best zip: ${zipCode.value} from ${zipCode.origin}\n`,
        `context RSN fee: '${appContextRsnFee}',\n`,
        `cookie  RSN fee: '${getCookie("rsnFee")}',\n`,
        `dma: '${dma}',\n`,
        `nielsenDma: '${nielsenDma}'`
      );

      if (zipCode.origin === "context" || zipCode.origin === "currentZipcode") {
        setInputZip(zipCode.value);

        // must be from other zipcode update, so clear cache
        if (zipCode.origin === "context") {
          zipcodeInputRef?.current?.clear();
        }
      }

      if (!(dma || nielsenDma)) {
        // on load with edgescape data
        setIsVisible(true);
        if (inputRef?.current?.focus) {
          inputRef.current.focus();
        }
      }

      handleCountySearch({
        searchZip: zipCode.value,
        trigger: "syncload"
      });
    };

    const handleCountySearch = ({
      searchZip,
      trigger
    }: {
      searchZip: string;
      trigger: string;
    }) => {
      if (searchZip === cachedZipcode) {
        console.log("Found same zip, setting cached values");
        setCountyOptions(lastCountyOptions);
        // A reset ("Clear page" / the ZIP field's clear icon) nulls countySelection but
        // leaves cachedZipcode, so re-entering the SAME zip lands here with the options
        // list restored and nothing selected. The fetch path below auto-selects a
        // single-county zip; this shortcut skipped that, and a one-option dropdown is
        // deliberately non-interactive (text-field-dropdown hides the chevron and drops
        // pointer events for it), so the agent was left with an empty county they could
        // not open and "Update" refused to continue. Multi-county zips are unaffected:
        // their dropdown stays openable, so the agent picks as usual.
        if (!countySelection && lastCountyOptions?.length === 1) {
          setCountySelection(lastCountyOptions?.[0]);
          setCountySelectionError(false);
        }
        return;
      }
      if (isCountyApiLoading) {
        console.debug("[ZipcodeModule] County API is already loading");
        return;
      }

      // -- Look County info in Context - Back Sync - skip getCounties
      const countiesInContext = countyOptionsValue;
      try {
        if (
          trigger === "syncload" &&
          appContextZip &&
          countiesInContext?.length && countiesInContext?.[0] && countiesInContext?.[0]?.zipcode === appContextZip
        ) {
          if (
            (!countyOptions?.length) ||
            (countyOptions?.length && countyOptions?.[0] && countiesInContext?.[0]?.zipcode !== countyOptions?.[0]?.zipcode)
          ) {
            setDisplayCounty(countiesInContext[0]);
            setCountySelection(countiesInContext[0]);
            setCountyOptions(countiesInContext);
            setLastCountyOptions(countiesInContext);
            setCachedDmas({
              dma: countiesInContext[0]?.dtvDMA,
              nielsenDma: countiesInContext[0]?.nielsenDMA
            });
          }
          cacheLocaleData(appContextZip, countiesInContext[0], true)
            .then(({ resLocalRsns, stale }) => {
              if (stale) {
                return;
              }
              onCacheConfirmed(
                appContextZip,
                countiesInContext,
                appContextRsnFee,
                resLocalRsns
              );
            })
            .catch(err => {
              console.error(
                "[ZipcodeModule] cacheLocaleData error in context back sync:",
                err
              );
            });
          return;
        }
      } catch (err) {
        console.error(
          "[ZipcodeModule] Error during context county back sync:",
          err
        );
      }

      // -- Set existing values to null + loading state
      setCountyOptions([]);
      setCountyApiError(false);
      setCountyApiHanging(false);
      setCachedZipcode("");
      setCachedDmas(null);
      setIsCountyApiLoading(true);
      getCounties(searchZip)
        .then(({ fetchedCounties, fetchedZip }) => {
          console.debug("[ZipcodeModule] got counties >>", fetchedCounties);
          if (!fetchedCounties?.length) {
            setCountyNoResults(true);
            setIsCountyApiLoading(false);
            return;
          }
          // -- Earliest a zipcode can be confirmed as valid, so set cookie
          setCachedZipcode(fetchedZip);
          setCountyNoResults(false);
          setIsCountyApiLoading(false);
          setCountyApiHanging(false);

          // -- Update Context Counties when trigger is "syncload" and Context Counties are empty
          if (trigger === "syncload" && (!countiesInContext?.length || !appContextZip)) {
            updateCountyOptions(fetchedCounties);
            // if context zipcode is empty, along with counties update context zipcode
            if (!zipCodeValue) {
              const rsnFeeCookie = getCookie("rsnFee");
              if (rsnFeeCookie) {
                updateZipCode(fetchedZip, false);
                updateRsnFeeCtx(rsnFeeCookie);
              }
            }
          }

          // -- Always set options
          setCountyOptions(fetchedCounties);
          setLastCountyOptions(fetchedCounties);
          // Reset county selection if multiple counties and not triggered by syncload
          if (fetchedCounties?.length > 1 && trigger !== "syncload") {
            setCountySelection(null);
            // Do NOT clear displayCounty here; keep previous county visible while loading
          }
          let countyToSelect: County | null = null;
          let countyToPrefetch: County = fetchedCounties?.[0];

          // -- Always display the county if one fetched
          if (fetchedCounties?.length === 1) {
            countyToSelect = fetchedCounties?.[0];
            setCountySelectionError(false);
          }

          if (trigger === "syncload") {
            // -- If triggered from page load or context sync (NOT classic search) and
            //    only one county matches existing DMA (usually multiple, but not always), select it.
            // -- Otherwise, select the first county.
            // -- later enhancement: select exact county based on cookie
            const matchingDma: County[] = fetchedCounties?.filter(
              c => c?.dtvDMA === getDmaCookies()?.currentDma
            );
            if (matchingDma?.length === 1) {
              countyToSelect = matchingDma?.[0];
              countyToPrefetch = countyToSelect;
              setDisplayCounty(countyToSelect);
            } else {
              setDisplayCounty(fetchedCounties?.[0]);
            }
          }

          if (countyToSelect !== null) {
            setCountySelection(countyToSelect);
            setCachedDmas({
              dma: countyToSelect?.dtvDMA,
              nielsenDma: countyToSelect?.nielsenDMA
            });
          }

          // -- Fetch Local RSN channels to callback or cache for later
          return cacheLocaleData(
            fetchedZip,
            countyToPrefetch,
            Boolean(trigger === "syncload")
          ).then(({ resLocalRsns, stale }) => {
            if (trigger === "syncload" && !stale) {
              onCacheConfirmed(
                fetchedZip,
                fetchedCounties,
                appContextRsnFee,
                resLocalRsns
              );
            }
          });
        })
        .catch(() => {
          // If county API fails, should we call Epoch anyways?
          setCountyApiError(true);
          setCountyApiHanging(false);
          setCountyNoResults(false);
          setIsCountyApiLoading(false);

          // If county API fails, try to get EPOCH anyways
          try {
            if (isValidRsnFee(appContextRsnFee)) {
              onRsnFeeConfirmed(appContextRsnFee);
            }
          } catch (error) {
            console.error("[ZipcodeModule] Failed to confirm RSN fee from context.", error);
          }
        });
    };

    /** Fetch the first county's local/RSN channels for a new ZIP
     *  - on page load, send them to the parent; always cache them (cacheKey),
     *    failures included
     *  - reuses only a lineup cached for this exact ZIP + market (RSNs depend
     *    on the ZIP), then clears the rest
     */
    const cacheLocaleData = async (
      searchZip: string,
      firstCounty: County,
      onLoad: boolean
    ) => {
      localeRequestSeq.current += 1;
      const requestSeq = localeRequestSeq.current;
      // Reuse only this exact ZIP + market: RSNs depend on the ZIP.
      const key = cacheKey(searchZip, firstCounty);
      const cachedLineup = cachedLocalRsn?.[key];
      const localRsnPromise = cachedLineup ?
        Promise.resolve(cachedLineup) :
        lookUp(searchZip, firstCounty);

      setCachedLocalRsn({});
      if (cachedLineup) {
        storeLineup(key, cachedLineup);
      }
      // eslint-disable-next-line no-return-await
      return await Promise.allSettled([localRsnPromise]).then(results => {
        const resLocalRsns =
          results?.[0]?.status === "fulfilled" ? results?.[0]?.value : null;
        const superseded = requestSeq !== localeRequestSeq.current;
        if (superseded && localeDeliveredSeq.current > requestSeq) {
          return {
            resLocalRsns,
            stale: true
          };
        }

        console.debug(
          `[ZipcodeModule] got local rsn channels, ${
            onLoad ?
              "caching and sending callback" :
              "caching data only"
          } >>`,
          resLocalRsns
        );

        if (superseded) {
          // Only this lookup's own page-load delivery; a pending Update click is
          // waiting for the newer lookup, not this one.
          if (onLoad) {
            deliverLineup(resLocalRsns, firstCounty);
            localeDeliveredSeq.current = requestSeq;
          }
        } else if (onLoad) {
          deliverLineup(resLocalRsns, firstCounty);
          localeDeliveredSeq.current = requestSeq;
          setLocalRsnCallbackNeeded(false);
        }
        // Otherwise a pending Update gets the cached result through the lagged
        // effect, and only if it is the lookup the visitor confirmed.

        return {
          resLocalRsns,
          stale: false
        };
      });
    };
    /** ---------------------------------------------------------------------------------------- */

    /* --- Input Handlers --- */
    /** On County Dropdown change:
     * - update county selection
     * - if the county's DMAs differ, update cachedDmas (DMA cookies)
     * - if this ZIP + market has no lineup cached or in flight, look it up
     */
    const handleCountySelectionChange = val => {
      const countyObj = countyOptions?.find(
        item => item?.getKey() === val?.value
      );
      setCountySelection(countyObj);
      setCountySelectionError(false);

      // Use Case: user enters a zip, removes part of it, then changes county
      // If the county is not found, reset the selection
      if (
        inputZip?.length < 5 &&
        cachedZipcode?.includes(inputZip) &&
        cachedZipcode !== ""
      ) {
        setInputZip(cachedZipcode);
      }

      // cachedDmas follows the selected county; it only feeds the DMA cookies.
      if (
        cachedDmas?.dma !== countyObj?.dtvDMA ||
        cachedDmas?.nielsenDma !== countyObj?.nielsenDMA
      ) {
        setCachedDmas({
          dma: countyObj?.dtvDMA,
          nielsenDma: countyObj?.nielsenDMA
        });
      }
      // Look this ZIP + market up unless it is cached or has no market (lookUp
      // reuses a request already in flight). The lookup always settles, so an
      // Update waiting for it is never left waiting.
      const key = cacheKey(cachedZipcode, countyObj);
      if (onLocalRsnChnlsConfirmed && key && !cachedLocalRsn?.[key]) {
        // Supersedes an in-flight cacheLocaleData prefetch.
        localeRequestSeq.current += 1;
        if (cachedLocalRsn?.[key] === null) {
          // A retry: drop the cached failure so an Update now waits for it.
          setCachedLocalRsn(prev => {
            const { [key]: _failed, ...rest } = prev ?? {};
            return rest;
          });
        }
        // getLocalRsnChannels has already reported a failure.
        lookUp(cachedZipcode, countyObj)?.catch(() => undefined);
      }
    };

    const handleZipInputChange = (e: any) => {
      const zipText = e?.target?.value; // ZipcodeValidator.stripNonNumbers()?.slice(0, 5);
      setInputZip(zipText);
      setZipInputError(false);

      // startEmpty starts blank — compare the freshly-typed value (not the stale
      // inputZip closure) so the first full ZIP reliably fires the county lookup.
      if (ZipcodeValidator.isValidFull(zipText) && (startEmpty ? zipText !== displayZip : inputZip !== displayZip)) {
        handleCountySearch({
          searchZip: zipText,
          trigger: "userinput"
        });
      }
    };

    const handleSearchButtonClick = async () => {
      if (!ZipcodeValidator.isValidFull(inputZip)) {
        setZipInputError(true);
        return;
      }
      if (countySelection === null) {
        setCountySelectionError(true);
        return;
      }

      console.debug(
        `[ZipcodeModule] handlingSearchButtonClick\n`,
        `cachedZip: ${cachedZipcode}\n`,
        `county: `,
        countySelection,
        `previous RSN fee: ${appContextRsnFee}\n`,
        `needsToSendLocalRsnCallback: ${localRsnCallbackNeeded}\n`,
        `cachedLocalRsn:`,
        cachedLocalRsn
      );

      if (cachedZipcode !== "") {
        let success = true;
        setInputZip(cachedZipcode);
        setDisplayZip(cachedZipcode);
        setDisplayCounty(countySelection);
        // Deliver only the result cached for this ZIP and the confirmed county's
        // market. A county with no market settles at once with empty lists.
        const confirmedKey = cacheKey(cachedZipcode, countySelection);
        setConfirmed({
          key: confirmedKey,
          county: countySelection
        });
        const settled = !confirmedKey || cachedLocalRsn?.[confirmedKey] !== undefined;
        const confirmedLineup = cachedLocalRsn?.[confirmedKey] ?? null;

        onSearchButtonClick(
          cachedZipcode,
          countyOptions,
          appContextRsnFee,
          confirmedLineup,
          countySelection
        );
        if (updateZipCode !== undefined) {
          updateZipCode(cachedZipcode, true);
          updateCountyOptions(countyOptions);
        }

        if (isValidRsnFee(appContextRsnFee)) {
          console.debug(
            "[ZipcodeModule] sent rsn fee callback (search click)",
            appContextRsnFee
          );
          try {
            onRsnFeeConfirmed(appContextRsnFee);
          } catch {
            success = false;
          }
        }

        if (settled) {
          console.debug(
            "[ZipcodeModule] sent local/rsn callback (search click)"
          );
          try {
            deliverLineup(confirmedLineup, countySelection);
            localeDeliveredSeq.current = localeRequestSeq.current;
          } catch {
            success = false;
          }
          if (!confirmedLineup) {
            success = false;
          }
          setLocalRsnCallbackNeeded(false);
        } else {
          success = false;
          setLocalRsnCallbackNeeded(true);
        }

        if (cachedDmas?.dma !== "" && cachedDmas?.nielsenDma !== "") {
          setDmaCookies(cachedDmas?.dma, cachedDmas?.nielsenDma);
        }

        console.log(
          `[ZipcodeModule] Send rsn fee and local/rsn's on search buttonClick >> ${
            success ? "SUCCESS" : "NOT READY"
          }`
        );
        if (success) {
          triggerSuccessNotification();
        }
      }

      // WAT-12478 — GTM auto-capture reports this click from the CTA's
      // aria-label / data-linkposition / data-linkdestination.
    };

    const toggleVisibility = () => {
      const cmpElem = document.querySelector(`#${id}`) as HTMLElement;
      const distToTop = cmpElem?.getBoundingClientRect()?.top || 0;
      if (
        !isVisible &&
        (distToTop < 100 || distToTop > window.innerHeight / 2)
      ) {
        window.scrollBy({
          top: distToTop - window.innerHeight / 4,
          behavior: "smooth"
        });
      }
      setIsVisible(prev => !prev);
    };

    const onZipcodeCleared = () => {
      setInputZip("");
      setZipInputError(false);
      setCountySelection(null);
    };

    const triggerSuccessNotification = () => {
      setShowSuccessMsg(true);

      // clear existing timeout
      if (successMsgTimeoutRef?.current) {
        clearTimeout(successMsgTimeoutRef.current);
      }
      successMsgTimeoutRef.current = setTimeout(() => {
        setShowSuccessMsg(false);
        setIsVisible(false);
        successMsgTimeoutRef.current = null;
      }, SUCCESS_MSG_TIMEOUT_MS);
    };

    /** ---------------------------------------------------------------------------------------- */
    /** Render */
    /* ----------------------------------------------------------------------------------------
     *  --- Render Starts */
    // Load incoming initializer data
    //  - Should mimic the same flow as syncIncomingData -> handleCountySearch -> cacheLocaleData
    useEffect(() => {
      if (isConditional && initData?.zipcode) {
        setDisplayZip(initData?.zipcode || "");

        if (
          initData?.zipcode === (getCookie("currentZipcode") || "") ||
          initData?.zipcode === appContextZip
        ) {
          setInputZip(initData?.zipcode || "");
        }

        if (initData?.counties && initData?.counties?.length > 0) {
          setCountyOptions(initData?.counties);
          setLastCountyOptions(initData?.counties);
          if (initData?.counties?.length > 1 && initData?.county) {
            const cnty = initData?.counties?.find(
              c => c.getKey() === initData?.county.getKey()
            );
            setCountySelection(cnty);
            setDisplayCounty(cnty);
          } else {
            setCountySelection(initData?.counties?.[0]);
            setDisplayCounty(initData?.counties?.[0]);
          }
        // Generated by Copilot- Start - 2026-04-13 14:06:05
        } else if (initData?.zipcode) {
          // No counties pre-provided but zipcode is available — trigger county lookup.
          handleCountySearch({
            searchZip: initData.zipcode,
            trigger: 'syncload'
          });
        } else {
          setCountyApiError(true);
        }
        // Generated by Copilot- End - 2026-04-13 14:06:05
      }
    // Generated by Copilot- Start - 2026-04-15 15:50:32
    }, [isConditional, initData?.zipcode, initData?.counties, initData?.county]);
    // Generated by Copilot- End - 2026-04-15 15:50:32

    /** Call sync to load incoming data (both on load and on context update) */
    useEffect(() => {
      // Generated by Copilot- Start - 2026-04-15 14:21:57
      if (isConditional && initData?.zipcode) {
        return;
      }
      // Generated by Copilot- End - 2026-04-15 14:21:57

      // startEmpty (agent tool): keep the field blank until the user enters a
      // ZIP — skip the on-load/context sync that auto-fills from edgescape/cookie.
      if (startEmpty) {
        return;
      }

      const { currentDma, nielsenDma } = getDmaCookies();
      // Generated by Copilot- Start - 2026-04-15 13:56:21
      const resolvedZip = appContextZip || "";
      // Generated by Copilot- End - 2026-04-15 13:56:21
      syncIncomingData({
        contextZip: resolvedZip,
        contextFee: appContextRsnFee || "",
        dma: currentDma || "",
        nielsenDma: nielsenDma || ""
      });
    // Generated by Copilot- Start - 2026-04-15 15:50:32
    }, [appContextZip, isConditional, initData?.zipcode, startEmpty]);
    // Generated by Copilot- End - 2026-04-15 15:50:32

    // startEmpty (agent tool): open the input drawer on mount so the blank ZIP
    // field is visible and ready for the user to type into.
    useEffect(() => {
      if (startEmpty) {
        setIsVisible(true);
      }
    }, [startEmpty]);

    // Parent-driven reset (agent tool's "Clear page"). Guarded on a CHANGE in the
    // signal, not a truthy value, so mounting isn't a reset and a re-render can't
    // wipe a ZIP just typed. App context / DMA cookies are left alone: under
    // startEmpty the sync effect above no-ops, so the local field is the load state.
    const prevResetSignalRef = React.useRef(resetSignal);
    useEffect(() => {
      if (resetSignal === prevResetSignalRef.current) {
        return;
      }
      prevResetSignalRef.current = resetSignal;
      const initialZip = startEmpty ?
        "" :
        ((isConditional && initData?.zipcode) ? initData.zipcode : (appContextZip || ""));
      setDisplayZip(initialZip);
      setInputZip(initialZip);
      setZipInputError(false);
      setDisplayCounty(null);
      setCountySelection(null);
      // The Fusion field keeps its own internal cache alongside the controlled
      // `value` — same clear the context-origin sync path uses (see syncIncomingData).
      zipcodeInputRef?.current?.clear();
      if (startEmpty) {
        setIsVisible(true);
      }
    }, [resetSignal, startEmpty, isConditional, initData?.zipcode, appContextZip]);

    // Whether the per-ZIP RSN fee is allowed to reach the parent yet. A boolean,
    // not the ZIP itself -- see the note inside the effect.
    //
    // The startEmpty arm requires the opt-in as well, and that is the whole point
    // of the prop. Base suppressed the fee PERMANENTLY for every startEmpty
    // consumer; opening it on submit alone would start delivering fees to v1 of
    // the agent tool, which passes startEmpty and never asked for them. A fresher
    // fee is not the same as a requested one, and this module is shared.
    const feeGateOpen = !startEmpty || (perZipRsnFee && Boolean(displayZip));

    // Once RSN Fee fetched in Context Send it to parent
    useEffect(() => {
      // startEmpty (agent tool): don't surface a stale, previously-cached RSN
      // fee before the agent has manually submitted a zip code.
      //
      // WAT-11842 item 22 — this used to be a bare `if (startEmpty) return`,
      // which is permanent rather than "until the first submission", and that is
      // the one path a REFRESHED fee travels on. The search-click callback at
      // handleSearchButtonClick forwards `appContextRsnFee` read from its own
      // render closure -- the PRE-search value, as that handler's own debug line
      // calls it -- while the correct per-ZIP fee arrives a tick later: the
      // application-context provider's `updateZipCode(zip, true)` fetches it in a
      // background task and writes both context and cookie, which re-renders us
      // and re-runs this effect. Gated on startEmpty alone, that fresh value was
      // dropped every time, so the agent tool's fee lagged one submission behind
      // and rendered nothing at all on the first search from a clean session.
      //
      // `displayZip` is the module's existing "a valid ZIP has been submitted"
      // signal — the CTA label already derives Add-vs-Update location from it. All
      // five of its writes were audited for reachability under startEmpty, because
      // borrowing a signal other code paths also own is the risk here: the initial
      // state and the parent-driven reset both write "" under startEmpty; the sync
      // effect returns early under startEmpty and is syncIncomingData's only
      // caller; the initData branch needs `isConditional`, which neither agent
      // tool passes; which leaves handleSearchButtonClick. So under startEmpty the
      // search handler is the only thing that can open this gate.
      //
      // Two reasons it beats a dedicated ref: it is a DEPENDENCY of this effect, so
      // a fee that happens to equal its mount value still re-runs and still reaches
      // the parent; and the parent-driven reset above clears it, so "Clear page"
      // closes the stale-fee gate again instead of leaving it open for good.
      //
      // Known and accepted: on a SECOND search, this fires once with the previous
      // ZIP's fee before the new one lands, then corrects itself. Waiting for the
      // context value to CHANGE would remove that flash, and would also deliver
      // nothing at all whenever two ZIPs carry the same fee — which is the common
      // case. A stale value for one render beats no value at all, and the real fix
      // belongs upstream: the context provider could clear the fee while it
      // refetches, and `isValidRsnFee` would hold this gate shut for that window.
      //
      // The gate is collapsed to a BOOLEAN before it reaches the dependency array
      // on purpose. Every other consumer of this module leaves startEmpty at its
      // default false, so for them the gate is permanently open and depending on
      // the ZIP string itself would be pure extra effect runs -- and those runs
      // are not free downstream: dtv-plan-card-topper hands the fee to
      // handleRsnFeeChange, which calls setDynamicCardData with a fresh object
      // literal, so React's Object.is bail-out cannot fire and a live plan-card
      // surface re-renders for a value that never changed. As a boolean the dep
      // is constant true for those consumers and only transitions for the agent
      // tools that actually asked for the gate.
      if (!feeGateOpen) {
        return;
      }
      if (isValidRsnFee(appContextRsnFee)) {
        onRsnFeeConfirmed(appContextRsnFee);
        console.debug("[ZipcodeModule] received RSN fee:", appContextRsnFee);
      } else if (perZipRsnFee) {
        // Logged for the same reason the success branch three lines up is: a
        // retraction moves a quoted price and left no trace at all. It does NOT
        // disambiguate "this ZIP has no regional-sports market" from "the fee
        // lookup failed" - the provider's catch produces '' for both and the
        // module never sees the error - so it records THAT a retraction happened,
        // not why. The module's unused onRsnFeeApiError prop is the seam for the
        // why, and wiring it needs an error signal the app context does not
        // expose today; named in the PR rather than half-built here.
        //
        // warn, not debug: this line is the only trace of a retraction that MOVES
        // a quoted monthly total, and console.debug is filtered out of the console
        // an agent or a support engineer is actually looking at. The success branch
        // above stays at debug deliberately - it records a value arriving, which is
        // the ordinary case and needs no attention.
        console.warn("[ZipcodeModule] retracting RSN fee - no valid value for this ZIP:", appContextRsnFee);
        // WAT-11842 blocker: a ZIP that resolves NO fee has to RETRACT the
        // previous ZIP's figure, and this is the only place that can. The
        // search-click handler cannot: it forwards the PRE-search value (see
        // the closure note above), so at click time it still holds the old
        // fee and any check there passes on stale data.
        //
        // This fires because the provider genuinely clears the value rather
        // than leaving the last one in place: application-context's
        // updateZipCode throws on !isValidRsnFee(fee) and its catch runs
        // `setRsnFee('')`, so appContextRsnFee transitions (e.g. '13.99' ->
        // ''), this effect's own dependency changes, and we re-run. Two ZIPs
        // that share a fee still never re-run, which is correct: the value
        // already on screen is the right one.
        //
        // Behind the SAME opt-in that gates delivery above, not behind
        // startEmpty, because this is a CONTRACT change and startEmpty does
        // not mean what it would need to mean. Both halves share one prop
        // because they are one contract: a consumer that re-decides the fee
        // per ZIP wants the refreshed value AND the retraction, and a consumer
        // that does not want either. startEmpty says "do not auto-fill the
        // ZIP"; v1 of the agent
        // tool passes it too (dtv-agent-package-compare-tool.tsx:450) and its
        // handler reads anything that is not a fee as a FAULT — it logs
        // `[agentTool] Zipcode Module returned a faulty rsn fee ... This is a
        // defect.` and forces the fee to "0". Gating on startEmpty would have
        // fired that line on every clean session and poisoned the one channel
        // v1 uses to report real RSN defects. The plan-card surfaces are the
        // other reason: they rely on the write-only behaviour that ships today,
        // and retracting for them would blank a live price surface nobody asked
        // to change. v2 is the only caller that opts in, and it is the one
        // quoting a second customer on the same browser session.
        onRsnFeeConfirmed(undefined);
      }
    }, [appContextRsnFee, feeGateOpen, perZipRsnFee]);

    useEffect(() => {
      const confirmedLineup = cachedLocalRsn?.[confirmed?.key];
      if (localRsnCallbackNeeded === true && confirmed?.key && confirmedLineup !== undefined) {
        console.debug(
          "[ZipcodeModule] sent local/rsn's to subscriber (lagged callback)"
        );
        try {
          // A failed lookup ends the wait (see deliverLineup) with no success message.
          deliverLineup(confirmedLineup, confirmed?.county);
          if (confirmedLineup) {
            triggerSuccessNotification();
          }
        } catch {
          // A throwing page must not leave the wait on, or this re-sends on every change.
          setLocalRsnCallbackNeeded(false);
          return;
        }

        // On failure isLocalsEligible is left as it was.
        if (confirmedLineup) {
          const isLocalsEligible = Boolean(
            confirmedLineup?.localChannels?.length > 0
          )?.toString();
          window.sessionStorage.setItem("isLocalsEligible", isLocalsEligible);
        }

        setLocalRsnCallbackNeeded(false);
      }
    }, [localRsnCallbackNeeded, cachedLocalRsn, confirmed]);

    useEffect(() => {
      setIsInIframe(window.self !== window.top);
    }, []);
    /** --------------------------------------------------------------------- */

    const countySelectionProp = useMemo(
      () => countySelection?.getKey() || "",
      [countySelection]
    );

    const countyOptionProps = useMemo(() => (inputZip?.length === 5 && countyOptions?.length ?
      countyOptions?.map((op: County) => ({
        value: op.getKey(),
        label: op.getLabel()
      })) :
      []), [countyOptions]);

    const zipCodeMessage = useMemo(() => {
      if (zipInputError) {
        return zipCodeInputErrorMsg;
      } else if (countyNoResults) {
        return zipCodeLookupErrorMsg;
      }
      return "";
    }, [
      zipInputError,
      zipCodeInputErrorMsg,
      countyNoResults,
      zipCodeLookupErrorMsg
    ]);

    const countyMessage = useMemo(() => {
      if (countySelectionError) {
        return countySelectionErrorMsg;
      }
      return "";
    }, [countySelectionError, countySelectionErrorMsg]);

    const dynamicNotificationStyle = React.useCallback(
      showMsgCondition => ({
        overflow: "hidden",
        // [WAT-12043 / YFH-AMP115] `maxHeight: 0` + `overflow: hidden`
        // visually hides the notification but leaves it in the a11y tree,
        // so NVDA announces the aria-label "Update Successful" / "Error"
        // even while the row is off-screen. `display: none` fully removes
        // the element from the tree per WCAG (SC 4.1.2). Applied on the
        // SAME condition that drives maxHeight to 0.
        ...((!isVisible || !showMsgCondition) && { display: "none" }),
        "& .MuiPaper-root .MuiAlert-message": { overflow: "hidden" }, // hide rogue scrollbar
        "& .MuiTypography-root": { fontFamily: "roboto-medium !important" }, // override "Roboto"
        maxHeight:
          !isVisible || !showMsgCondition ? 0 : {
            xs: "120px",
            md: "80px"
          }, // value slightly more than max height on viewport
        "& .MuiPaper-root": {
          alignItems: "center",
          maxHeight:
            !isVisible || !showMsgCondition ? 0 : {
              xs: "120px",
              md: "80px"
            }, // value slightly more than max height on viewport
          margin: "0px 0px !important"
        },
        "& .MuiAlert-standardSuccess, & .MuiAlert-standardError": {
          margin: "0 !important"
        },

        ...(!isVisible ?
          {
            // hide message with drawer collapse
            transition: `max-height 100ms cubic-bezier(0.4, 0, 0.2, 1)`,
            "& .MuiPaper-root": {
              transition: `max-height 100ms cubic-bezier(0.4, 0, 0.2, 1)`,
              "& .MuiAlert-icon svg": {
                maxHeight: 0,
                opacity: 0,
                transition:
                    "max-height 100ms cubic-bezier(0.4, 0, 0.2, 1) 100ms, opacity 100ms cubic-bezier(0.4, 0, 0.2, 1)"
              }
            }
          } :
          !showMsgCondition ?
            {
              // hide message timeout animation
              // perspective sets how "far" the object is from camera in 3D space. Rotating down from -90deg with transformOrigin top gives a "fall down" effect
              transform:
                "perspective(1000px) rotateX(-90deg) translateY(0) translateZ(0)",
              // delay transform/max-height animations so they are "secret"
              transition: `transform 0ms cubic-bezier(0, .5, 1, 1) 230ms, max-height 200ms cubic-bezier(0.4, 0, 0.2, 1) 230ms`,
              "& .MuiPaper-root": {
                position: "relative",
                top: "-110%",
                // slide top/margin up first, then collapse height/padding
                transition: `top 230ms cubic-bezier(0.4, 0, 0.2, 1), margin 180ms cubic-bezier(0.4, 0, 0.2, 1) 50ms,
            max-height 100ms cubic-bezier(0.4, 0, 0.2, 1) 230ms, padding 100ms cubic-bezier(0.4, 0, 0.2, 1) 230ms`,
                "& .MuiAlert-icon svg": {
                  opacity: 0,
                  maxHeight: 0,
                  transition:
                    "max-height 180ms cubic-bezier(0.4, 0, 0.2, 1) 50ms, opacity 180ms cubic-bezier(0.4, 0, 0.2, 1) 50ms"
                }
              }
            } :
            {
              // show message animation
              transform:
                "perspective(1000px) rotateX(0deg) translateY(0) translateZ(0)",
              // timing starts faster
              transition: `transform 180ms cubic-bezier(0, .5, 1, 1), max-height 100ms cubic-bezier(0, 0.5, 1, 1)`,
              "& .MuiPaper-root": {
                position: "relative",
                top: 0
              }
            })
      }),
      [isVisible, countyApiError, countyApiHanging, showSuccessMsg]
    );

    return (
      <DtvBox
        id={id}
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: hAlignment,
          marginBottom: "5px"
        }}
      >
        <DtvBox
          id={`${id}-flex-wrp`}
          sx={{
            width: "100%",
            containerType: "inline-size",
            containerName: "zmRoot",
            display: "flex",
            flexDirection: "column",
            alignItems: hAlignment,
            zIndex: 900,
            ...(position === "center" && {
              maxWidth: "900px",
              [down(bps.values.md)]: {
                maxWidth: "700px"
              }
            })
          }}
        >
          {/** Header Text */}
          <DtvBox
            id={`${id}-header-ctnr`}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              [up(bps.values.sm)]: {
                flexDirection: "row"
              },
              [down(bps.values.sm)]: {
                padding: position === "center" ? "0px 12px" : "0px"
              },
              alignItems: "center",
              justifyContent: hAlignment
            }}
          >
            {/* Grouped clickable text */}
            <DtvBox
              id="zm-dynamic-block"
              role="button"
              tabIndex={0}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                flexWrap: "wrap",
                justifyContent: hAlignment,
                cursor: "pointer"
              }}
              onBlur={() => setChangeFocused(false)}
              onClick={toggleVisibility}
              onFocus={() => setChangeFocused(true)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleVisibility();
                }
              }}
            >
              {/* "Showing details for:" */}
              <DtvText
                component="div"
                content={displayText || "Showing details for:"}
                variant={displayTextVariant}
                sx={{
                  color: mainTextColor,
                  [down(bps.values.sm)]: {
                    fontSize: position === "left" ? "16px" : "20px"
                  },
                  [up(bps.values.md)]: {
                    fontSize: position === "left" ? "20px" : "24px"
                  }
                }}
              />

              {/* Location pill: pin icon + "County, State ZIP" (or placeholder) —
               *  Figma "Tertiary button-tng", node 13987:15991 "ZIP Code locator" */}
              <DtvBox
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  borderRadius: "9999px",
                  padding: "0 8px",
                  marginLeft: "4px",
                  ...(position === "left" && { flexWrap: "nowrap" })
                }}
              >
                <LocationPinIcon color={changeTextColor} />
                <DtvText
                  component="span"
                  content={locationPillLabel}
                  variant={displayTextVariant}
                  sx={{
                    color: changeTextColor,
                    [down(bps.values.sm)]: {
                      fontSize: position === "left" ? "16px" : "20px"
                    },
                    [up(bps.values.md)]: {
                      fontSize: position === "left" ? "20px" : "24px"
                    },
                    display: "inline-flex"
                  }}
                />
                {displayTooltip?.content && (
                  <DtvText
                    content=""
                    sx={{ pl: "4px" }}
                    tooltip={{
                      ...displayTooltip,
                      sx: { color: mainTextColor }
                    }}
                  />
                )}
              </DtvBox>

              {/* Toggle Icon — Figma "up"/"down" chevron, single asset flipped via rotation */}
              <DtvBox
                aria-label={!isVisible ? drawerOpenText : drawerCloseText}
                id={"hide-chng-ctnr"}
                role="button"
                tabIndex={0}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: "pointer",
                  transform: !isVisible ? "rotate(0deg)" : "rotate(180deg)",
                  transition: "transform 200ms ease-in-out",
                  padding: "6px",
                  marginLeft: "4px",
                  "& span": {
                    fontSize: "16px !important",
                    fontFamily: "roboto-medium !important"
                  },
                  lineHeight: "24px",
                  border: "none !important",
                  fontFamily: "roboto-medium !important"
                }}
              >
                {/* Figma "Icon" wrapper is 20x20 with the chevron vector inset to
                 *  ~13.89x8.33 — fixed size, no responsive variant in the design. */}
                <svg
                  fill="none"
                  height="8"
                  viewBox="0 0 13.8889 8.33333"
                  width="14"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.0109371 1.37519C0.0109371 1.02926 0.136982 0.677671 0.394539 0.41114C0.909652 -0.11625 1.74261 -0.11625 2.25224 0.41114L6.94306 5.27674L11.6449 0.399796C12.16 -0.133265 12.9874 -0.133265 13.5026 0.399796C14.0177 0.932858 14.0177 1.78916 13.5026 2.32222L8.2418 7.77759C7.90205 8.12918 7.43077 8.33333 6.93758 8.33333C6.44986 8.33333 5.97311 8.13486 5.63335 7.78327L0.383602 2.33923C0.126045 2.0727 0 1.72678 0 1.38085"
                    fill={changeTextColor}
                  />
                </svg>
              </DtvBox>
            </DtvBox>
          </DtvBox>

          {/** Toggled Content */}
          <DtvBox
            id={`${id}-toggled-content`}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
              justifyContent: hAlignment,
              width: "100%",
              flexWrap: "nowrap",
              [position === "left" ?
                down(bps.values.sm) :
                bps.down(bps.values.md)]: {
                flexWrap: "wrap"
              },
              ...(isVisible ?
                {
                    overflow: "visible",
                    maxHeight: "230px",
                    paddingTop: "10px",
                    [up(bps.values.sm)]: {
                      maxHeight: "250px",
                      paddingTop: "12px"
                    },
                    opacity: 1,
                    // opening
                    transition: `max-height 226ms cubic-bezier(0.4, 0, 0.2, 1), padding 96ms ease-out 50ms, opacity 256ms cubic-bezier(0.4, 0, 0.2, 1)`
                  } :
                {
                    overflow: "hidden",
                    maxHeight: "0px",
                    paddingTop: "0px",
                    opacity: 0,
                    // closing
                    transition: `max-height 226ms cubic-bezier(0.6, 0, 0.4, 1) 50ms, padding 96ms ease-in 100ms, opacity 256ms cubic-bezier(0.2, 0, 0.4, 1) 50ms`
                  })
            }}
          >
            {/** Input Fields */}
            <DtvBox
              id={`${id}-inputs-container`}
              sx={{
                width: "100%",
                [up(bps.values.sm)]: {
                  display: "flex",
                  flexDirection: "row"
                },
                [down(bps.values.sm)]: {
                  display: "flex",
                  flexDirection: "column"
                }
              }}
            >
              <DtvBox
                id={`${id}-zip-ctnr`}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexBasis: "100%",
                  padding: 0,
                  [up(bps.values.sm)]: {
                    padding: "0px 12px 0px 0px !important",
                    flexBasis: "200px"
                  },
                  transition: "padding 100ms ease",
                  fontFamily: "roboto-regular !imoprtant",
                  // Base <div> tag
                  "& .MuiFormControl-root, & .DtvFormControl-root": {
                    height: "48px",
                    [bps.up(bps.values.md)]: {
                      height: size === "small" ? "48px" : "56px !important"
                    },
                    transition: `height 196ms ${
                      isVisible ?
                        "cubic-bezier(0.4, 0, 0.2, 1)" :
                        "cubic-bezier(0.2, 0, 0.4, 1)"
                    }`
                  },
                  "& .MuiInputBase-root": {
                    height: "48px",
                    [bps.up(bps.values.md)]: {
                      height: size === "small" ? "48px" : "56px !important"
                    }
                  },
                  "& .MuiInputLabel-root, .MuiInputLabel-shrink, .MuiFormLabel-root":
                    {
                      fontFamily: "roboto-medium !important"
                    }
                }}
              >
                <TextField
                  helperMessageIcon
                  showClearIcon
                  aria-label={zipCodeLabel ? zipCodeLabel : "enter zip code"}
                  error={Boolean(zipCodeMessage)}
                  helperMessageText={zipCodeMessage}
                  iconVariant="clearIcon"
                  label={zipCodeLabel}
                  mode={mode}
                  role="textbox"
                  showHelperMessage={Boolean(zipCodeMessage)}
                  size={size}
                  tabIndex={isVisible ? 0 : -1}
                  type="zipcode"
                  value={inputZip}
                  width="100%"
                  inputProps={{
                    role: "textbox",
                    "aria-label": inputZip
                  }}
                  onChange={handleZipInputChange}
                  onClear={onZipcodeCleared}
                />
              </DtvBox>
              <DtvBox
                id={`${id}-county-ctnr`}
                sx={{
                  display: "flex",
                  width: "100%",
                  backgroundColor: "transparent",
                  flexBasis: "100%",
                  padding: "12px 0 0 0",
                  [up(bps.values.sm)]: {
                    padding: "0 12px 0 0",
                    // fixedCountyWidth (agent tool only): Figma node 13987:15915
                    // "Dropdown" is a fixed 256px, not fluid/remaining-space.
                    // Every other consumer keeps the original fluid width.
                    ...(fixedCountyWidth ?
                      {
                          flexBasis: "256px",
                          flexGrow: 0,
                          flexShrink: 0
                        } :
                      { flexBasis: "calc(100% - 200px)" })
                  },
                  transition: `flex-basis 200ms ease`,
                  fontFamily: "roboto-regular !important",
                  // Use roboto-regular for label/placeholder only
                  "& .MuiInputLabel-root, & .MuiInputLabel-shrink, & .MuiFormLabel-root":
                    {
                      fontFamily: "roboto-medium !important"
                    }
                }}
              >
                <TextFieldDropdown
                  helperMessageIcon
                  error={Boolean(countyMessage)}
                  helperMessageText={countyMessage}
                  id="county-dropdown"
                  isLoadingOptions={isCountyApiLoading}
                  isVisible={isVisible}
                  label={countyLabel}
                  maxVisible={3}
                  mode={mode}
                  options={countyOptionProps}
                  showHelperMessage={Boolean(countyMessage)}
                  size={size}
                  value={countySelectionProp}
                  width="100%"
                  onSelection={handleCountySelectionChange}
                />
              </DtvBox>
            </DtvBox>
            {/** CTA button — Figma "Secondary button-tng" (outlined pill).
             *  Label switches from "Add location" to "Update location" once a
             *  location has already been submitted at least once. */}
            <Button
              isTangerine
              aria-label={ctaLinkName || `${(displayZip ? ctaLabel : addLocationCtaLabel)?.toLocaleLowerCase?.()}`}
              buttontype="secondary"
              data-linkposition={isInIframe ? modalLinkPosition("channel lineup") : ctaLinkPosition || "body"}
              data-linkdestination={locationHrefurl}
              mode={mode}
              size={size}
              tabIndex={isVisible ? 0 : -1}
              {...(isCountyApiLoading && { disabled: true })}
              sx={{
                minWidth: "unset !important",
                width: "auto",
                margin: "0",
                fontFamily: "roboto-medium !important",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "130%",
                ...(position === "left" && {
 marginBottom: {
 xs: "4px",
sm: "12px"
}
}),
                height: size === "small" ? "48px" : "56px",
                padding: size === "small" ? "4px 20px" : "4px 32px",
                fontSize: "16px",
                ...(position === "left" ?
                  {
                      [down(bps.values.md)]: {
                        height: "48px",
                        padding: "4px 20px",
                        fontSize: "16px"
                      },
                      [down(bps.values.sm)]: {
                        height: "36px",
                        padding: "4px 20px",
                        fontSize: "14px"
                      }
                    } :
                  {
                      [down(bps.values.md)]: {
                        height: "48px",
                        padding: "4px 20px",
                        fontSize: "16px"
                      },
                      [down(bps.values.sm)]: {
                        height: "36px",
                        padding: "4px 20px",
                        fontSize: "14px"
                      }
                    }),

                [up(bps.values.md)]: {
                  transition: `height 196ms ${
                    isVisible ?
                      "cubic-bezier(0.4, 0, 0.2, 1)" :
                      "cubic-bezier(0.2, 0, 0.4, 1)"
                  }`
                },

                "& span": {
                  fontFamily: "roboto-medium"
                }
              }}
              onClick={handleSearchButtonClick}
            >
              {displayZip ? ctaLabel : addLocationCtaLabel}
            </Button>
          </DtvBox>

          {/** Notifications */}
          <DtvBox
            aria-label={UPDATE_SUCCESS_MSG}
            sx={{
              width: "100%",
              margin: "8px 0 0 0",
              ...(position === "center" && {
                maxWidth: "900px",
                [down(bps.values.sm)]: { maxWidth: "700px" }
              }),
              ...dynamicNotificationStyle(showSuccessMsg)
            }}
          >
            <DtvAlert
              oneRow
              ariaLabel={UPDATE_SUCCESS_MSG}
              severity="success"
              title={UPDATE_SUCCESS_MSG}
            />
          </DtvBox>
          <DtvBox
            aria-label="Error"
            sx={{
              width: "100%",
              margin: "8px 0 0 0",
              ...(position === "center" && {
                maxWidth: "900px",
                [down(bps.values.sm)]: { maxWidth: "700px" }
              }),
              ...dynamicNotificationStyle(
                (countyApiError || countyApiHanging) && !showSuccessMsg
              )
            }}
          >
            <DtvAlert
              oneRow
              severity="error"
              ariaLabel={
                countyApiError ?
                  countyApiErrorMsg :
                  countyApiHanging ?
                  countyApiHangingMsg :
                  ""
              }
              title={
                countyApiError ?
                  countyApiErrorMsg :
                  countyApiHanging ?
                  countyApiHangingMsg :
                  ""
              }
            />
          </DtvBox>
          {/* [WAT-12043 / YFH-AMP121 — DEFERRED] A persistent sr-only
              `role="status"` region briefly shipped here to satisfy the
              audit's "always-mounted empty live region" template. Review
              caught that Fusion's DtvAlert wraps MUI Alert (implicit
              `role="alert"`), so a successful ZIP update announced twice
              in a row on NVDA/JAWS ("Update Successful" from the alert,
              then again from the status region). Region removed pending
              a UX call on which announcer wins (tracked as the AMP121
              follow-up ticket). The visible DtvAlerts still fire
              `role="alert"` on mount, which is the pre-PR behaviour. */}
        </DtvBox>
      </DtvBox>
    );
  } catch (error) {
    console.error("ZipCode Module >> Component Error:", error);
    return <></>;
  }
});
